// /finance (finance redesign slice 3, DD-039, DD-041): the agency's money, one question per tab. To do (the default):
// what must I do with the money. Clients: which client earns what. Jobs and Invoices (finance consolidation S8, the user
// 2026-10-22): every client's jobs and invoices, each with its statuses on the header's right and the client page's
// drawers; Invoices lists the team invoices too, one person's invoice to the agency, their claims, which replace Pay as
// the payout (TD-073). Team: who do I owe. Business expenses: what running BeNerdy costs. Cash: where we stand, some days
// back and ahead (slice 4, cash where-we-stand). Price list: the website's "from" prices, back from Setup (the user
// 2026-10-30), with Setup's old link coming here. Below md the eight tabs are DD-057's burger sheet, the current one named
// beside it. Every admin sees the page, but only those DD-040's exception lets in see the money tabs and their drawers:
// the rest see Price list alone, and a link naming another tab or a money drawer lands on it. The expense categories
// stay in Setup.
// Every row opens its drawer on this page (DD-051) and never navigates. One drawer at a time, named in the URL, and a
// drawer opened from a drawer replaces it (DD-045). A job (`modal=line-job&lineJob=`, AgencyLineJobDrawer), a client
// invoice (`modal=job-invoice&invoice=`, AgencyJobInvoiceDrawer), a team invoice (`modal=team-invoice&teamInvoice=`) and a
// person (`modal=person&person=`, their team invoices, New team invoice stacked over it) open on every tab, so a Team row,
// a job on an invoice or a claim's job opens over the tab the user is on. To do, Cash and Clients read jobs, claims and
// invoices too (finance consolidation S9), so main's contract-month drawers are retired from every click here: the
// month, its invoice, Send, Mark paid, Edit split, Pay, a statement, Split pay, Who does what and an expired quote's. A
// Clients row opens the client's drawer (`modal=client&client=`): its jobs done or under way and its invoices, each opening its
// drawer in the client's place, and New invoice stacked over it (`stack=new-invoice`), which To do's To invoice opens too
// with the server's done jobs ticked (`pick=`). A pot month's line out of the pot opens its client's drawer. Closing the
// last drawer a row opened puts focus back on that row, or on the tab's first row once it left (useOpenerFocus).
import { useRef } from "react";
import { PhoneNavSheet } from "@/components/Header/PhoneNavSheet";
import { PageHeader, PageLayout } from "@/components/PageLayout";
import { Button } from "@/components/ui/Button";
import { TabGroup } from "@/components/ui/TabGroup";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { useAppStore } from "@/core/store/useAppStore";
import { ExpenseEntryModal } from "../expenses/ExpenseEntryModal";
import { ExpenseItemModal } from "../expenses/ExpenseItemModal";
import { ExpenseModal } from "../expenses/ExpenseModal";
import { canSeeFinanceTabs } from "../financeAccess";
import { useAgencyExpenses } from "../hooks/useAgencyFinance";
import { useFinanceParams, type ParamPatch } from "../hooks/useFinanceParams";
import { useListini } from "../hooks/usePricing";
import { AdSpendModal } from "../money/AdSpendModal";
import { PricesTab, selectedListino } from "../prices/PricesTab";
import { AddFreelancerModal } from "../team/AddFreelancerModal";
import { BusinessExpensesTab } from "./BusinessExpensesTab";
import { AgencyInvoicesMenu, AgencyInvoicesShowTabs, AgencyInvoicesTab } from "./AgencyInvoicesTab";
import { AgencyJobsShowTabs, AgencyJobsTab, AgencyNewJobButton } from "./AgencyJobsTab";
import { AgencyJobInvoiceDrawer } from "./AgencyJobInvoiceDrawer";
import { AgencyLineJobDrawer } from "./AgencyLineJobDrawer";
import { ClientDrawer } from "./ClientDrawer";
import { CashRange, CashTab } from "./CashTab";
import { DEFAULT_DAYS, toDays } from "./cashText";
import { useOpenClient } from "./clientLink";
import { ClientsShowTabs, ClientsTab } from "./ClientsTab";
import { PersonDrawer } from "./PersonDrawer";
import { PotDrawer, PotMonthDrawer } from "./PotDrawers";
import { TeamInvoiceDrawer } from "./TeamInvoiceDrawer";
import { TeamTab } from "./TeamTab";
import { ToDoTab } from "./ToDoTab";
import { useOpenerFocus } from "./useOpenerFocus";

// shrink-0: a tab keeps its whole label. On a phone the track scrolls sideways instead of truncating them.
const TABS = [
  { id: "todo", label: "To do" },
  { id: "cash", label: "Cash" },
  { id: "clients", label: ENTITY_CONFIG.client.plural },
  // The user's job, a line item, rides main's `contract` key (the entityConfig shadow).
  { id: "jobs", label: ENTITY_CONFIG.contract.plural },
  { id: "invoices", label: ENTITY_CONFIG.invoice.plural },
  { id: "team", label: "Team" },
  { id: "expenses", label: `Business ${ENTITY_CONFIG.expense.plural.toLowerCase()}` },
  { id: "prices", label: "Price list" },
].map((t) => ({ ...t, className: "shrink-0" }));
// What an admin outside DD-040's exception sees: the price list alone.
const OPEN_TABS = TABS.filter((t) => t.id === "prices");

const TAB_CONFIG = { variant: "navigation", size: "sm", layout: "horizontal", hasBackground: true } as const;

type Tab = "todo" | "cash" | "clients" | "jobs" | "invoices" | "team" | "expenses" | "prices";
const TAB_IDS: Tab[] = ["todo", "cash", "clients", "jobs", "invoices", "team", "expenses", "prices"];
/** The tab the URL names, To do by default; without the money tabs, always Price list. */
const toTab = (value: string | null, full: boolean): Tab => (full ? TAB_IDS.find((t) => t === value) ?? "todo" : "prices");

// Opening a drawer clears every other drawer param in the same call: one set per handler.
const NO_MODAL: ParamPatch = {
  modal: null, client: null, job: null, person: null, stmt: null, entry: null, tpl: null, confirm: null, category: null, contract: null, work: null, month: null, invoice: null, lineJob: null,
  teamInvoice: null, stack: null, pick: null,
};
// A tab change also clears the filters, Cash's range and the price list's selection, which each tab reads its own way.
const NO_FILTERS: ParamPatch = { show: null, q: null, days: null, listino: null, line: null, copy: null, item: null };

export function AgencyFinancePage() {
  const { params, set, closeModals } = useFinanceParams();
  const openClient = useOpenClient();
  const full = canSeeFinanceTabs(useAppStore((state) => state.user?.email));
  const tabs = full ? TABS : OPEN_TABS;
  const tab = toTab(params.get("tab"), full);
  const modal = params.get("modal");
  const clientId = params.get("client");
  const personId = params.get("person");
  const entryId = params.get("entry");
  const templateId = params.get("tpl");
  // With no opener left on the page (its status moved the row, a pick emptied the tab, a link or a reload opened the
  // drawer), focus goes to the tab's first row, else its create button, else the tab itself: the strip's, or below md the
  // burger beside its name (the S8 walk).
  const pageRef = useRef<HTMLDivElement>(null);
  const rememberOpener = useOpenerFocus(modal, () => [
    pageRef.current?.querySelector<HTMLElement>('tbody tr[tabindex="0"]'),
    pageRef.current?.querySelector<HTMLElement>("[data-opener-fallback] button"),
    pageRef.current?.querySelector<HTMLElement>('[data-opener-tabs="strip"] button[aria-current="true"]'),
    pageRef.current?.querySelector<HTMLElement>('[data-opener-tabs="sheet"] button'),
  ]);
  // Cash's range: 30 days unless the URL names another the picker offers.
  const days = toDays(params.get("days"));
  // The ads and pot drawers read Business expenses' list here, from the cache its tab filled (finance-real round 8, slice 12 R19).
  const expenses = useAgencyExpenses(full && (modal === "ads" || modal === "pot" || modal === "pot-month"));
  const pot = expenses.data?.pots.find((p) => p.id === params.get("category"));
  const potMonth = pot?.months.find((m) => m.month === params.get("month"));
  // Add service line needs a listino to add to: the one PricesTab shows, from the same cached read.
  const viewedListino = selectedListino(useListini(tab === "prices").data?.listini ?? [], params.get("listino"));
  /** From a row: this drawer alone. */
  const open = (patch: ParamPatch) => set({ ...NO_MODAL, ...patch });
  /** From a drawer: the next one replaces it, on the same client (DD-045). */
  const go = (patch: ParamPatch, replace?: boolean) => set({ ...NO_MODAL, client: clientId, ...patch }, replace);
  const close = () => closeModals(NO_MODAL);
  /** From a row: that drawer, and focus back on the row once it closes. */
  const openFromRow = (patch: ParamPatch) => {
    rememberOpener();
    open(patch);
  };
  /** A tab that opens its own drawers marks its row first, while no drawer is open: a hand-over keeps the first opener. */
  const markOpener = () => {
    if (modal === null) rememberOpener();
  };
  const pickTab = (id: string) => set({ ...NO_MODAL, ...NO_FILTERS, tab: id === "todo" ? null : id });
  /** From the price list's header: its catalogs and Add service line, which PricesTab names in the URL as Setup did. */
  const openPrices = (patch: ParamPatch) => {
    markOpener();
    set(patch);
  };
  return (
    <PageLayout>
      {/* `contents` leaves the layout as it was: the ref only scopes where focus falls back to. */}
      <div ref={pageRef} className="contents">
        <PageHeader>
          <PageHeader.Row className="flex-wrap gap-y-2">
            {/* Seven tabs pass a phone's width: below md they are DD-057's burger sheet, the current one named beside it;
                from md up the strip. */}
            <div className="min-w-0 max-w-full">
              <div className="md:hidden flex items-center gap-2" data-opener-tabs="sheet">
                <PhoneNavSheet title={ENTITY_CONFIG.finance.label} items={tabs} activeId={tab} onSelect={pickTab} />
                <span className="text-sm font-semibold text-gray-900">{tabs.find((t) => t.id === tab)?.label}</span>
              </div>
              <div className="hidden md:block min-w-0 max-w-full" data-opener-tabs="strip">
                <TabGroup items={tabs} activeId={tab} onTabChange={pickTab} config={TAB_CONFIG} className="min-w-0 max-w-full overflow-x-auto" />
              </div>
            </div>
            {/* What the page lists on the left, Clients' status on the right (finance-real round 7, as the Library pairs them). */}
            {tab === "clients" && (
              <PageHeader.Actions>
                <ClientsShowTabs config={TAB_CONFIG} />
              </PageHeader.Actions>
            )}
            {tab === "jobs" && (
              <PageHeader.Actions>
                <AgencyJobsShowTabs config={TAB_CONFIG} />
                {/* New job, its client picked first, Agency (no client) listed first (the user 2026-10-24). */}
                <span data-opener-fallback className="contents">
                  <AgencyNewJobButton onOpen={markOpener} />
                </span>
              </PageHeader.Actions>
            )}
            {tab === "invoices" && (
              <PageHeader.Actions>
                <AgencyInvoicesShowTabs config={TAB_CONFIG} />
                {/* New client invoice and New team invoice, each picking whom first (the user 2026-10-22). */}
                <span data-opener-fallback className="contents">
                  <AgencyInvoicesMenu onOpen={markOpener} />
                </span>
              </PageHeader.Actions>
            )}
            {tab === "cash" && (
              <PageHeader.Actions>
                <CashRange days={days} onDays={(n) => set({ days: n === DEFAULT_DAYS ? null : String(n) }, true)} />
              </PageHeader.Actions>
            )}
            {/* DD-056: a header create button is an EntityCreateButton the size of the Buttons beside it. */}
            {tab === "team" && (
              <PageHeader.Actions>
                <span data-opener-fallback className="contents">
                  <EntityCreateButton entity="freelancer" variant="primary" size="sm" onClick={() => open({ modal: "add-freelancer" })} />
                </span>
              </PageHeader.Actions>
            )}
            {/* The expense categories are Setup's (finance-real round 2, R1). */}
            {tab === "expenses" && (
              <PageHeader.Actions>
                <span data-opener-fallback className="contents">
                  <EntityCreateButton entity="expense" variant="primary" size="sm" onClick={() => open({ modal: "add-expense" })} />
                </span>
              </PageHeader.Actions>
            )}
            {tab === "prices" && (
              <PageHeader.Actions>
                {/* The catalogs open in their own drawers (DD-042: the tab stays one table). */}
                <Button size="sm" variant="secondary" onClick={() => openPrices({ modal: "service-lines" })}>
                  {ENTITY_CONFIG.serviceLine.plural}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => openPrices({ modal: "work-types" })}>
                  {ENTITY_CONFIG.workType.plural}
                </Button>
                {viewedListino && (
                  <span data-opener-fallback className="contents">
                    <EntityCreateButton entity="serviceLine" variant="primary" size="sm" onClick={() => openPrices({ modal: "add-line", line: null, confirm: null, copy: null })} />
                  </span>
                )}
              </PageHeader.Actions>
            )}
          </PageHeader.Row>
        </PageHeader>
        {tab === "todo" && <ToDoTab onOpen={openFromRow} />}
        {tab === "cash" && <CashTab days={days} onOpen={openFromRow} />}
        {tab === "clients" && (
          <ClientsTab
            onOpen={(id) => {
              rememberOpener();
              open({ modal: "client", client: id });
            }}
          />
        )}
        {tab === "jobs" && <AgencyJobsTab onOpen={markOpener} />}
        {tab === "invoices" && <AgencyInvoicesTab onOpen={markOpener} />}
        {tab === "team" && <TeamTab onOpen={open} />}
        {tab === "expenses" && <BusinessExpensesTab onOpen={open} />}
        {tab === "prices" && <PricesTab />}
      </div>

      {/* The money drawers, behind DD-040's exception as the money tabs are. A link naming no client lands with none. */}
      {full && modal === "client" && clientId && (
        <ClientDrawer
          key={clientId}
          clientId={clientId}
          onClose={close}
          onOpenPage={() => openClient.page(clientId)}
          // A job or an invoice, listed or just made, in the client's place (DD-045), on the page's drawers, on this tab.
          onOpenJob={(id) => set({ ...NO_MODAL, modal: "line-job", lineJob: id }, true)}
          onOpenInvoice={(id) => set({ ...NO_MODAL, modal: "job-invoice", invoice: id }, true)}
          creating={params.get("stack") === "new-invoice"}
          // To do's row names the server's done jobs to tick (`pick=`); closing New invoice drops them.
          picks={params.get("pick")?.split(",").filter(Boolean)}
          onCreate={(on) => set({ stack: on ? "new-invoice" : null, pick: null }, true)}
        />
      )}
      {full && modal === "job-invoice" && params.get("invoice") && (
        <AgencyJobInvoiceDrawer
          key={params.get("invoice")}
          invoiceId={params.get("invoice") ?? ""}
          paying={params.get("confirm") === "paid"}
          stacked={params.get("stack") === "line-job-form"}
          onStack={(on) => set({ stack: on ? "line-job-form" : null }, true)}
          // A job on it opens its drawer in place of this one, on the tab the user is on (DD-045): closing it leaves them there.
          onOpenJob={(id) => set({ ...NO_MODAL, modal: "line-job", lineJob: id }, true)}
          onClose={close}
        />
      )}
      {full && modal === "line-job" && params.get("lineJob") && (
        <AgencyLineJobDrawer
          key={params.get("lineJob")}
          jobId={params.get("lineJob") ?? ""}
          starting={params.get("confirm") === "start"}
          // Its invoice in its place (DD-045), on the page's invoice drawer.
          onOpenInvoice={(id) => set({ ...NO_MODAL, modal: "job-invoice", invoice: id }, true)}
          onClose={close}
        />
      )}
      {full && modal === "team-invoice" && params.get("teamInvoice") && (
        <TeamInvoiceDrawer
          key={params.get("teamInvoice")}
          invoiceId={params.get("teamInvoice") ?? ""}
          paying={params.get("confirm") === "paid"}
          addingJob={params.get("stack") === "line-job-form"}
          // A person's New job opens it with `client=agency`: the form starts on the agency's own client.
          onAgency={params.get("client") === "agency"}
          onAddJob={(on) => set({ stack: on ? "line-job-form" : null, client: null }, true)}
          // A claim's job opens its drawer in place of this one, on the tab the user is on (DD-045).
          onOpenJob={(id) => set({ ...NO_MODAL, modal: "line-job", lineJob: id }, true)}
          onClose={close}
        />
      )}
      {full && modal === "person" && personId && (
        <PersonDrawer
          key={personId}
          personId={personId}
          creating={params.get("stack") === "new-team-invoice"}
          onCreate={(on) => set({ stack: on ? "new-team-invoice" : null }, true)}
          // A team invoice, made here or listed, in the person's place (DD-045).
          onOpenInvoice={(id) => set({ ...NO_MODAL, modal: "team-invoice", teamInvoice: id }, true)}
          // Their draft with the job form over it: the team invoice's New job, in the person's place.
          onNewJob={(id) => set({ ...NO_MODAL, modal: "team-invoice", teamInvoice: id, stack: "line-job-form", client: "agency" }, true)}
          onClose={close}
        />
      )}
      {full && modal === "add-freelancer" && <AddFreelancerModal onClose={close} />}
      {full && modal === "expense" && entryId && <ExpenseEntryModal key={entryId} entryId={entryId} onClose={close} />}
      {full && modal === "expense-item" && templateId && <ExpenseItemModal key={templateId} templateId={templateId} confirm={params.get("confirm")} onClose={close} />}
      {full && modal === "add-expense" && <ExpenseModal onClose={close} />}
      {full && modal === "ads" && entryId && expenses.data && (
        <AdSpendModal key={entryId} entryId={entryId} data={{ adSpend: expenses.data.adSpend, today: expenses.data.today }} onClose={close} />
      )}
      {/* A pot and its month (finance-real round 8): a row in either opens its own drawer in its place (DD-045, slice 12 R13). */}
      {full && modal === "pot" && pot && <PotDrawer key={pot.id} pot={pot} onOpen={(patch) => go(patch, true)} onClose={close} />}
      {full && modal === "pot-month" && pot && potMonth && (
        <PotMonthDrawer key={`${pot.id}-${potMonth.month}`} pot={pot} month={potMonth} onOpen={(patch) => go(patch, true)} onClose={close} />
      )}
    </PageLayout>
  );
}
